import React, { useCallback } from 'react';
import { IInputMaskFieldProps } from '../types';
import MaskInput from '../../inputMask';
import { DEFAULT_REQUIRED_ERROR } from '../index';
import { fieldsValidate } from '../utils/fieldValidation';
import getFieldError from '../utils/getFieldError';

const InputMaskField: React.FC<IInputMaskFieldProps> = props => {
	const {
		field: {
			// for field
			fieldName,
			defaultValue,
			value,
			hotReload,
			onChange,
			onBlur,
			// for validation
			minLength,
			maxLength,
			validationType,
			isRequired,
			errorMessage,
			regexpPattern,
            minValue,
            maxValue,
            minMaxMessage,
			// other
			...rest
		},
		formHook: {
			register,
			setValue,
			formState: { errors },
		},
	} = props;

	const message: string | undefined = getFieldError(fieldName, errors);

	const validationHandler = useCallback(
		(validateValue: string) => fieldsValidate({ value: validateValue, validationType, minLength, maxLength, regexpPattern, minValue, maxValue, minMaxMessage }),
		[validationType, minLength, maxLength, regexpPattern]
	);
	const fieldRegistration = React.useMemo(
		() =>
			register(fieldName, {
				required: isRequired ? errorMessage || DEFAULT_REQUIRED_ERROR : false,
				validate: (validateValue: string) => validationHandler(validateValue),
			}),
		[errorMessage, isRequired, fieldName, register, validationHandler]
	);

	const registrationWithUserProps = React.useMemo(
		() => ({
			...fieldRegistration,
			onChange: async (ev: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
				if (onChange) onChange(ev);
				await fieldRegistration.onChange(ev);
			},
			onBlur: async (ev: React.FocusEvent<HTMLInputElement>): Promise<void> => {
				if (onBlur) onBlur(ev);
				await fieldRegistration.onBlur(ev);
			},
		}),
		[fieldRegistration, onChange, onBlur]
	);

	React.useEffect(() => {
		if (!hotReload || defaultValue === undefined) return;
		setValue(fieldName, defaultValue);
	}, [fieldName, defaultValue, setValue, hotReload]);

	return <MaskInput {...rest} {...registrationWithUserProps} value={value} defaultValue={defaultValue} isInvalid={!!message} isRequired={isRequired} errorMessage={message} />;
};

export default React.memo(InputMaskField);
